import React, { useState, useEffect } from "react";
import { FiPlus, FiTrash2, FiEdit2, FiCheckCircle, FiClock, FiAlertCircle } from "react-icons/fi";
import { apiRequest, teamAPI } from "../../api/apiHelper";

const PIPELINE_STATUSES = ["Pending", "Overdue", "Completed"];

const STATUS_CONFIG = {
  Pending:   { bg: "#fffbf5", border: "#fde8c8", badge: "#f97316", icon: <FiClock size={13} />, light: "#fff7ed" },
  Completed: { bg: "#f6fef9", border: "#c3f0d0", badge: "#16a34a", icon: <FiCheckCircle size={13} />, light: "#f0fdf4" },
  Overdue:   { bg: "#fff5f5", border: "#fecaca", badge: "#dc2626", icon: <FiAlertCircle size={13} />, light: "#fef2f2" },
};

// Safely extract a display name from assignedTo (may be string id or object)
function getAssigneeName(task) {
  if (task.assignedUser?.name) return task.assignedUser.name;
  if (task.assignedUser?.email) return task.assignedUser.email;
  if (task.assignedTo && typeof task.assignedTo === "object") {
    return task.assignedTo.name || task.assignedTo.email || null;
  }
  return null;
}

// Safely get assignedTo id for form pre-fill
function getAssignedToId(task) {
  if (task.assignedTo && typeof task.assignedTo === "object") return task.assignedTo._id || task.assignedTo.id || "";
  if (typeof task.assignedTo === "string") return task.assignedTo;
  return "";
}

// Format milliseconds into "Xh Ym" or "Xm" string
function fmtDuration(ms) {
  const totalMin = Math.round(ms / 60000);
  if (totalMin <= 0) return null;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

// Safe ISO string conversion — returns null if invalid
function safeISO(val) {
  if (!val) return null;
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
  } catch (e) {
    return null;
  }
}

const PRESET_PCTS = [0.15, 0.45, 0.85]; // 15%, 45%, 85% before due

function ReminderSection({ dueDate, reminders, onChange }) {
  // reminders: array of ISO strings
  const [customVal, setCustomVal] = useState("");
  const [showCustom, setShowCustom] = useState(false);

  // Compute preset options from dueDate
  const presets = React.useMemo(() => {
    if (!dueDate) return [];
    const now = Date.now();
    const due = new Date(dueDate).getTime();
    if (isNaN(due) || due <= now) return [];
    const gap = due - now;
    return PRESET_PCTS.map(pct => {
      const ts = due - gap * pct;
      if (ts <= now) return null;
      const dur = fmtDuration(gap * pct);
      if (!dur) return null;
      const iso = safeISO(new Date(ts));
      if (!iso) return null;
      return { pct, iso, label: `${Math.round(pct * 100)}%`, dur };
    }).filter(Boolean);
  }, [dueDate]);

  const toggle = (iso) => {
    if (!iso) return;
    if (reminders.includes(iso)) {
      onChange(reminders.filter(r => r !== iso));
    } else {
      onChange([...reminders, iso]);
    }
  };

  const addCustom = () => {
    if (!customVal) return;
    const iso = safeISO(customVal);
    if (!iso) return;
    const due = new Date(dueDate).getTime();
    if (isNaN(due) || new Date(customVal).getTime() >= due) return;
    if (!reminders.includes(iso)) onChange([...reminders, iso]);
    setCustomVal("");
    setShowCustom(false);
  };

  const removeReminder = (iso) => onChange(reminders.filter(r => r !== iso));

  if (!dueDate) return (
    <div style={{ fontSize: "0.8rem", color: "#9ca3af", padding: "8px 0" }}>
      Set a due date first to configure reminders.
    </div>
  );

  return (
    <div>
      {/* Preset chips row */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
        {presets.map(p => {
          const active = reminders.includes(p.iso);
          return (
            <button
              key={p.pct}
              type="button"
              onClick={() => toggle(p.iso)}
              style={{
                padding: "5px 10px", borderRadius: 8, cursor: "pointer", fontSize: "0.75rem",
                fontWeight: 600, textAlign: "center", lineHeight: 1.3,
                border: active ? "2px solid var(--primary-color)" : "1.5px solid #d1d5db",
                background: active ? "#eff6ff" : "#f9fafb",
                color: active ? "var(--primary-color)" : "#374151",
                transition: "all 0.15s",
              }}
            >
              <div>{p.label}</div>
              <div style={{ fontSize: "0.68rem", fontWeight: 500, color: active ? "var(--primary-color)" : "#6b7280" }}>
                ({p.dur} before)
              </div>
            </button>
          );
        })}

        {/* Custom button */}
        <button
          type="button"
          onClick={() => setShowCustom(v => !v)}
          style={{
            padding: "5px 12px", borderRadius: 8, cursor: "pointer", fontSize: "0.75rem",
            fontWeight: 600, border: showCustom ? "2px solid #7c3aed" : "1.5px solid #d1d5db",
            background: showCustom ? "#f5f3ff" : "#f9fafb",
            color: showCustom ? "#7c3aed" : "#374151", transition: "all 0.15s",
          }}
        >
          Custom
        </button>
      </div>

      {/* Custom datetime picker */}
      {showCustom && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
          <input
            type="datetime-local"
            value={customVal}
            max={dueDate}
            onChange={e => setCustomVal(e.target.value)}
            style={{ flex: 1, padding: "8px 10px", borderRadius: 8, border: "1.5px solid #d1d5db", fontSize: "0.85rem", outline: "none" }}
          />
          <button
            type="button"
            onClick={addCustom}
            disabled={!customVal}
            style={{
              padding: "8px 14px", borderRadius: 8, border: "none",
              background: "var(--primary-color)", color: "#fff",
              fontWeight: 600, fontSize: "0.82rem", cursor: customVal ? "pointer" : "not-allowed",
              opacity: customVal ? 1 : 0.5,
            }}
          >
            Add
          </button>
        </div>
      )}

      {/* Selected reminders list */}
      {reminders.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
          {reminders.map(iso => {
            if (!iso) return null;
            const d = new Date(iso);
            if (isNaN(d.getTime())) return null;
            const label = d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
            return (
              <span key={iso} style={{
                display: "flex", alignItems: "center", gap: 5,
                background: "#eff6ff", color: "var(--primary-color)",
                border: "1px solid #bfdbfe", borderRadius: 20,
                padding: "3px 10px", fontSize: "0.75rem", fontWeight: 600,
              }}>
                🔔 {label}
                <button
                  type="button"
                  onClick={() => removeReminder(iso)}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", padding: 0, fontSize: 14, lineHeight: 1, marginLeft: 2 }}
                >×</button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TaskModal({ open, onClose, onSave, teamMembers, initialData }) {
  const isEdit = !!initialData;
  const empty = { taskTitle: "", tab: "Daily", dueDate: "", status: "Pending", assignedTo: "" };
  const [form, setForm] = useState(empty);
  const [reminders, setReminders] = useState([]); // array of ISO strings
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setForm({
        taskTitle: initialData.taskTitle || initialData.text || "",
        tab: initialData.tab || "Daily",
        dueDate: initialData.dueDate ? new Date(initialData.dueDate).toISOString().slice(0, 16) : "",
        status: initialData.status || "Pending",
        assignedTo: getAssignedToId(initialData),
      });
      // restore reminders: support both array and single reminderAt
      if (Array.isArray(initialData.reminders) && initialData.reminders.length > 0) {
        setReminders(initialData.reminders.map(r => safeISO(r)).filter(Boolean));
      } else if (initialData.reminderAt) {
        const iso = safeISO(initialData.reminderAt);
        setReminders(iso ? [iso] : []);
      } else {
        setReminders([]);
      }
    } else {
      setForm(empty);
      setReminders([]);
    }
  }, [open, initialData]);

  // When dueDate changes on a NEW task, auto-select the 3 presets
  // For edit mode, don't override existing reminders
  const prevDueDateRef = React.useRef("");
  useEffect(() => {
    if (isEdit) return; // don't auto-override when editing
    if (!form.dueDate) { setReminders([]); prevDueDateRef.current = ""; return; }
    if (form.dueDate === prevDueDateRef.current) return;
    prevDueDateRef.current = form.dueDate;
    const now = Date.now();
    const due = new Date(form.dueDate).getTime();
    if (isNaN(due) || due <= now) { setReminders([]); return; }
    const gap = due - now;
    const auto = PRESET_PCTS.map(pct => {
      const ts = due - gap * pct;
      if (ts <= now) return null;
      return safeISO(new Date(ts));
    }).filter(Boolean);
    setReminders(auto);
  }, [form.dueDate, isEdit]);

  if (!open) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.taskTitle.trim()) return;
    setSaving(true);
    try {
      const payload = {
        taskTitle: form.taskTitle.trim(),
        tab: form.tab,
        status: form.status,
        ...(form.dueDate && { dueDate: new Date(form.dueDate).toISOString() }),
        ...(reminders.length > 0 && {
          reminders,
          reminderAt: reminders[0], // keep backward compat with single field
        }),
        ...(form.assignedTo && { assignedTo: form.assignedTo }),
      };
      let res;
      if (isEdit) {
        res = await apiRequest("patch", "/tasks/update", { taskId: initialData._id || initialData.id, ...payload });
      } else {
        res = await apiRequest("post", "/tasks/create", payload);
      }
      if (res?.data) { onSave(res.data, isEdit); onClose(); }
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200 }}
      onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 16, width: 480, maxHeight: "92vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}
        onClick={e => e.stopPropagation()}>
        {/* Modal header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: "1.05rem", color: "#151d3b" }}>{isEdit ? "Edit Task" : "Create New Task"}</div>
            <div style={{ fontSize: "0.78rem", color: "#6b7280", marginTop: 2 }}>{isEdit ? "Update task details" : "Fill in the details to create a task"}</div>
          </div>
          <button onClick={onClose} style={{ background: "#f3f4f6", border: "none", cursor: "pointer", width: 32, height: 32, borderRadius: 8, fontSize: 18, color: "#6b7280", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: "20px 24px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={{ fontSize: "0.82rem", color: "#374151", fontWeight: 600, display: "block", marginBottom: 6 }}>Task Title *</label>
            <input value={form.taskTitle} onChange={e => setForm(p => ({ ...p, taskTitle: e.target.value }))}
              placeholder="e.g. Follow up with client..." required
              style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1.5px solid #e5e7eb", fontSize: "0.95rem", outline: "none", boxSizing: "border-box", transition: "border 0.2s" }}
              onFocus={e => e.target.style.borderColor = "var(--primary-color)"}
              onBlur={e => e.target.style.borderColor = "#e5e7eb"} />
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: "0.82rem", color: "#374151", fontWeight: 600, display: "block", marginBottom: 6 }}>Type</label>
              <select value={form.tab} onChange={e => setForm(p => ({ ...p, tab: e.target.value }))}
                style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1.5px solid #e5e7eb", fontSize: "0.95rem", outline: "none", background: "#fff" }}>
                {["Daily", "Weekly", "Monthly"].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: "0.82rem", color: "#374151", fontWeight: 600, display: "block", marginBottom: 6 }}>Status</label>
              <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
                style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1.5px solid #e5e7eb", fontSize: "0.95rem", outline: "none", background: "#fff" }}>
                {PIPELINE_STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={{ fontSize: "0.82rem", color: "#374151", fontWeight: 600, display: "block", marginBottom: 6 }}>Due Date</label>
            <input type="datetime-local" value={form.dueDate} onChange={e => setForm(p => ({ ...p, dueDate: e.target.value }))}
              style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1.5px solid #e5e7eb", fontSize: "0.9rem", outline: "none", boxSizing: "border-box" }} />
          </div>

          {/* Smart Reminder Section */}
          <div>
            <label style={{ fontSize: "0.82rem", color: "#374151", fontWeight: 600, display: "block", marginBottom: 8 }}>
              🔔 Reminders
              {reminders.length > 0 && (
                <span style={{ marginLeft: 8, background: "var(--primary-color)", color: "#fff", borderRadius: 20, padding: "1px 8px", fontSize: "0.7rem", fontWeight: 700 }}>
                  {reminders.length}
                </span>
              )}
            </label>
            <ReminderSection dueDate={form.dueDate} reminders={reminders} onChange={setReminders} />
          </div>

          {teamMembers.length > 0 && (
            <div>
              <label style={{ fontSize: "0.82rem", color: "#374151", fontWeight: 600, display: "block", marginBottom: 6 }}>Assign To</label>
              <select value={form.assignedTo} onChange={e => setForm(p => ({ ...p, assignedTo: e.target.value }))}
                style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1.5px solid #e5e7eb", fontSize: "0.95rem", outline: "none", background: "#fff" }}>
                <option value="">— Unassigned —</option>
                {teamMembers.map(m => <option key={m._id || m.id} value={m._id || m.id}>{m.name || m.email}</option>)}
              </select>
            </div>
          )}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", paddingTop: 8, borderTop: "1px solid #f0f0f0", marginTop: 4 }}>
            <button type="button" onClick={onClose}
              style={{ padding: "10px 24px", borderRadius: 8, border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", fontWeight: 500, color: "#6b7280", fontSize: "0.92rem", minWidth: 90 }}>
              Cancel
            </button>
            <button type="submit" disabled={saving}
              style={{ padding: "10px 28px", borderRadius: 8, border: "none", background: "var(--primary-color)", color: "#fff", cursor: saving ? "not-allowed" : "pointer", fontWeight: 600, fontSize: "0.92rem", minWidth: 120 }}>
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Task"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TaskCard({ task, onEdit, onDelete, onStatusChange }) {
  const fmt = (d) => {
    if (!d) return null;
    const date = new Date(d);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };
  const assigneeName = getAssigneeName(task);

  // Dedupe reminders: prefer the array, fall back to single reminderAt
  const reminderList = React.useMemo(() => {
    if (Array.isArray(task.reminders) && task.reminders.length > 0) {
      return task.reminders.map(r => safeISO(r)).filter(Boolean);
    }
    if (task.reminderAt) {
      const iso = safeISO(task.reminderAt);
      return iso ? [iso] : [];
    }
    return [];
  }, [task.reminders, task.reminderAt]);

  return (
    <div style={{
      background: "#fff", borderRadius: 10, border: "1px solid #e8eaed",
      padding: "14px 16px", marginBottom: 10,
      boxShadow: "0 1px 3px rgba(0,0,0,0.06)", transition: "all 0.2s",
    }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.1)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)"; e.currentTarget.style.transform = "translateY(0)"; }}
    >
      <div style={{ fontWeight: 600, fontSize: "0.92rem", color: "#111827", marginBottom: 8, wordBreak: "break-word", lineHeight: 1.4 }}>
        {task.taskTitle || task.text}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 10 }}>
        <span style={{ fontSize: "0.72rem", background: "#f1f5f9", color: "#475569", padding: "2px 8px", borderRadius: 20, fontWeight: 500 }}>
          {task.tab || "Daily"}
        </span>
        {task.dueDate && fmt(task.dueDate) && (
          <span style={{ fontSize: "0.72rem", color: "#6b7280", display: "flex", alignItems: "center", gap: 3 }}>
            📅 {fmt(task.dueDate)}
          </span>
        )}
        {reminderList.map((r, idx) => {
          const label = fmt(r);
          if (!label) return null;
          return (
            <span key={idx} style={{ fontSize: "0.72rem", color: "#7c3aed", display: "flex", alignItems: "center", gap: 3 }}>
              🔔 {label}
            </span>
          );
        })}
        {assigneeName && (
          <span style={{ fontSize: "0.72rem", color: "#1d4ed8", background: "#dbeafe", padding: "2px 7px", borderRadius: 20, fontWeight: 500 }}>
            👤 {assigneeName}
          </span>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 8, borderTop: "1px solid #f3f4f6" }}>
        <select value={task.status || "Pending"} onChange={e => onStatusChange(task._id || task.id, e.target.value)}
          style={{ fontSize: "0.75rem", padding: "4px 8px", borderRadius: 6, border: "1px solid #e5e7eb", cursor: "pointer", color: "#374151", background: "#f9fafb", fontWeight: 500 }}>
          {PIPELINE_STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
        <div style={{ display: "flex", gap: 2 }}>
          <button onClick={() => onEdit(task)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--primary-color)", padding: "4px 6px", borderRadius: 6, display: "flex", alignItems: "center" }}
            title="Edit task"><FiEdit2 size={14} /></button>
          <button onClick={() => onDelete(task._id || task.id)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", padding: "4px 6px", borderRadius: 6, display: "flex", alignItems: "center" }}
            title="Delete task"><FiTrash2 size={14} /></button>
        </div>
      </div>
    </div>
  );
}

export default function TasksPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [dateFilter, setDateFilter] = useState("All");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  useEffect(() => { loadTasks(); loadTeamMembers(); }, []);

  // Build date params from filter
  const buildDateParams = () => {
    if (dateFilter === "All" || !dateFilter) return {};
    if (dateFilter === "custom" && customStart && customEnd) {
      return { startDate: customStart, endDate: customEnd };
    }
    const days = Number(dateFilter);
    if (!isNaN(days) && days > 0) {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - days);
      return { startDate: start.toISOString().split("T")[0], endDate: end.toISOString().split("T")[0] };
    }
    return {};
  };

  async function loadTasks(params = {}) {
    try {
      const queryParams = { ...buildDateParams(), ...params };
      const res = await apiRequest("get", "/tasks/get", null, { params: queryParams });
      const raw = res?.data || [];
      setTasks(raw.map(t => ({
        ...t,
        _id: t._id || t.id,
        taskTitle: t.taskTitle || t.text || "",
        status: t.status || "Pending",
        tab: t.tab || "Daily",
      })));
    } catch (e) { console.error(e); }
  }

  // Re-fetch when date filter changes
  useEffect(() => {
    if (dateFilter === "custom" && (!customStart || !customEnd)) return;
    loadTasks();
  }, [dateFilter, customStart, customEnd]);

  async function loadTeamMembers() {
    try {
      const r = await teamAPI.getTeamMembers();
      let invites = r?.data?.invites || r?.data || r?.invites || [];
      if (!Array.isArray(invites)) invites = [];
      setTeamMembers(invites);
    } catch (e) { console.warn(e); }
  }

  function handleSaved(saved, isEdit) {
    const normalized = {
      ...saved,
      _id: saved._id || saved.id,
      taskTitle: saved.taskTitle || saved.text || "",
      status: saved.status || "Pending",
      tab: saved.tab || "Daily",
    };
    if (isEdit) {
      setTasks(prev => prev.map(t => (t._id === normalized._id || t.id === normalized._id) ? normalized : t));
    } else {
      setTasks(prev => [normalized, ...prev]);
    }
  }

  async function handleStatusChange(taskId, newStatus) {
    setTasks(prev => prev.map(t => (t._id === taskId || t.id === taskId) ? { ...t, status: newStatus } : t));
    try { await apiRequest("patch", "/tasks/update", { taskId, status: newStatus }); }
    catch (e) { console.error(e); loadTasks(); }
  }

  async function handleDelete(taskId) {
    if (!window.confirm("Delete this task?")) return;
    setTasks(prev => prev.filter(t => t._id !== taskId && t.id !== taskId));
    try { await apiRequest("patch", "/tasks/delete", { taskId }); }
    catch (e) { console.error(e); loadTasks(); }
  }

  function openEdit(task) { setEditTask(task); setModalOpen(true); }
  function openAdd() { setEditTask(null); setModalOpen(true); }
  function closeModal() { setModalOpen(false); setEditTask(null); }

  const totalTasks = tasks.length;
  const pendingCount = tasks.filter(t => t.status === "Pending").length;
  const completedCount = tasks.filter(t => t.status === "Completed").length;
  const overdueCount = tasks.filter(t => t.status === "Overdue").length;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", paddingBottom: 0 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 16, paddingTop: 8 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: "#111827" }}>Tasks</h2>
          <p style={{ margin: "4px 0 0", fontSize: "0.9rem", color: "#6b7280" }}>
            {totalTasks} total tasks
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {/* Date Range filter */}
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <label style={{ fontSize: "0.7rem", color: "#6b7280", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", marginLeft: 1 }}>Date Range</label>
            <select
              value={dateFilter}
              onChange={e => { setDateFilter(e.target.value); if (e.target.value !== "custom") { setCustomStart(""); setCustomEnd(""); } }}
              style={{
                padding: "8px 36px 8px 12px", borderRadius: 8,
                border: "1.5px solid #d1d5db", fontSize: "0.9rem",
                background: "#fff", cursor: "pointer", minWidth: 150,
                color: "#111827", fontWeight: 500,
                appearance: "none", WebkitAppearance: "none",
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 10px center",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              <option value="All">All</option>
              <option value="7">Last 7 Days</option>
              <option value="30">Last 30 Days</option>
              <option value="90">Last 90 Days</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>
          {dateFilter === "custom" && (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <label style={{ fontSize: "0.7rem", color: "#6b7280", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", marginLeft: 1 }}>Start</label>
                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                  style={{ padding: "8px 10px", borderRadius: 8, border: "1.5px solid #d1d5db", fontSize: "0.9rem", color: "#111827", fontWeight: 500, boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <label style={{ fontSize: "0.7rem", color: "#6b7280", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", marginLeft: 1 }}>End</label>
                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                  style={{ padding: "8px 10px", borderRadius: 8, border: "1.5px solid #d1d5db", fontSize: "0.9rem", color: "#111827", fontWeight: 500, boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }} />
              </div>
            </>
          )}
          {dateFilter !== "All" && (
            <button
              onClick={() => { setDateFilter("All"); setCustomStart(""); setCustomEnd(""); }}
              style={{
                padding: "8px 18px", borderRadius: 8,
                border: "1.5px solid #d1d5db", background: "#fff",
                cursor: "pointer", fontWeight: 600, color: "#374151",
                fontSize: "0.875rem", alignSelf: "flex-end",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                transition: "all 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#9ca3af"; e.currentTarget.style.background = "#f9fafb"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "#d1d5db"; e.currentTarget.style.background = "#fff"; }}
            >
              Clear Filters
            </button>
          )}
          <button onClick={openAdd} style={{
            background: "var(--primary-color)", color: "#fff", border: "none", borderRadius: 8,
            padding: "0 22px", fontWeight: 600, fontSize: "0.95rem", cursor: "pointer",
            display: "flex", alignItems: "center", gap: 8,
            boxShadow: "0 2px 8px rgba(30,42,90,0.15)", height: 42, flexShrink: 0,
            transition: "all 0.2s", alignSelf: "flex-end",
          }}
            onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
            onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}
          >
            <FiPlus style={{ fontSize: 18 }} /> Add Task
          </button>
        </div>
      </div>

      {/* Pipeline columns — stretch to fill width, full height, no horizontal scroll */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, flex: 1, height: "calc(100vh - 160px)", minHeight: 0, overflow: "hidden" }}>
        {PIPELINE_STATUSES.map(status => {
          const cfg = STATUS_CONFIG[status];
          const colTasks = tasks.filter(t => (t.status || "Pending") === status);
          return (
            <div key={status} style={{
              background: cfg.light, border: `1.5px solid ${cfg.border}`,
              borderRadius: 14, padding: "16px 14px",
              display: "flex", flexDirection: "column", minHeight: 0,
            }}>
              {/* Column header */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, flexShrink: 0 }}>
                <span style={{
                  background: cfg.badge, color: "#fff", borderRadius: 6,
                  padding: "4px 12px", fontWeight: 700, fontSize: "0.8rem",
                  display: "flex", alignItems: "center", gap: 5,
                }}>
                  {cfg.icon} {status}
                </span>
                <span style={{
                  marginLeft: "auto", background: "#fff", color: "#64748b",
                  borderRadius: 20, padding: "2px 10px", fontSize: "0.78rem", fontWeight: 700,
                  border: `1px solid ${cfg.border}`,
                }}>
                  {colTasks.length}
                </span>
              </div>
              {/* Scrollable cards area */}
              <div style={{ flex: 1, overflowY: "auto", paddingRight: 2 }}>
                {colTasks.length === 0 ? (
                  <div style={{
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    height: 120, color: "#9ca3af", fontSize: "0.85rem", gap: 8,
                  }}>
                    <div style={{ fontSize: 28, opacity: 0.4 }}>
                      {status === "Pending" ? "📋" : status === "Completed" ? "✅" : "⏰"}
                    </div>
                    No {status.toLowerCase()} tasks
                  </div>
                ) : (
                  colTasks.map(task => (
                    <TaskCard key={task._id || task.id} task={task}
                      onEdit={openEdit} onDelete={handleDelete} onStatusChange={handleStatusChange} />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      <TaskModal open={modalOpen} onClose={closeModal} onSave={handleSaved} teamMembers={teamMembers} initialData={editTask} />
    </div>
  );
}
